import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireRole } from '@/core/auth/RequireRole';
import { useAuth } from '@/core/auth/useAuth';
import { AppLayout } from '@/core/layout/AppLayout';
import { Loader } from '@/core/layout/Loader';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { ALL_PORTAL_ROLES, roleHomePath } from '@/core/permissions';

const AccountPage = lazy(() =>
  import('@/features/auth/pages/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/admin/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const OrganizationsPage = lazy(() =>
  import('@/features/admin/pages/OrganizationsPage').then((m) => ({
    default: m.OrganizationsPage,
  })),
);
const AssetsPage = lazy(() =>
  import('@/features/assets/pages/AssetsPage').then((m) => ({ default: m.AssetsPage })),
);
const AssetDetailPage = lazy(() =>
  import('@/features/assets/pages/AssetDetailPage').then((m) => ({
    default: m.AssetDetailPage,
  })),
);
const MembersPage = lazy(() =>
  import('@/features/members/pages/MembersPage').then((m) => ({ default: m.MembersPage })),
);
const MemberProfilePage = lazy(() =>
  import('@/features/members/pages/MemberProfilePage').then((m) => ({
    default: m.MemberProfilePage,
  })),
);
const ZonalPage = lazy(() =>
  import('@/features/zonal/pages/ZonalPage').then((m) => ({ default: m.ZonalPage })),
);
const ZoneDetailPage = lazy(() =>
  import('@/features/zonal/pages/ZoneDetailPage').then((m) => ({ default: m.ZoneDetailPage })),
);
const DepartmentsPage = lazy(() =>
  import('@/features/departments/pages/DepartmentsPage').then((m) => ({
    default: m.DepartmentsPage,
  })),
);
const DepartmentDetailPage = lazy(() =>
  import('@/features/departments/pages/DepartmentDetailPage').then((m) => ({
    default: m.DepartmentDetailPage,
  })),
);
const UnitDetailPage = lazy(() =>
  import('@/features/departments/pages/UnitDetailPage').then((m) => ({
    default: m.UnitDetailPage,
  })),
);
const EventsPage = lazy(() =>
  import('@/features/events/pages/EventsPage').then((m) => ({ default: m.EventsPage })),
);
const EventAttendancePage = lazy(() =>
  import('@/features/events/pages/EventAttendancePage').then((m) => ({
    default: m.EventAttendancePage,
  })),
);
const ChurchCalendarPage = lazy(() =>
  import('@/features/calendar/pages/ChurchCalendarPage').then((m) => ({
    default: m.ChurchCalendarPage,
  })),
);
const MemberImportPage = lazy(() =>
  import('@/features/members/pages/MemberImportPage').then((m) => ({
    default: m.MemberImportPage,
  })),
);
const CalendarImportPage = lazy(() =>
  import('@/features/calendar/pages/CalendarImportPage').then((m) => ({
    default: m.CalendarImportPage,
  })),
);
const AssetImportPage = lazy(() =>
  import('@/features/assets/pages/AssetImportPage').then((m) => ({
    default: m.AssetImportPage,
  })),
);
const ExpenseImportPage = lazy(() =>
  import('@/features/expenses/pages/ExpenseImportPage').then((m) => ({
    default: m.ExpenseImportPage,
  })),
);
const LocationsPage = lazy(() =>
  import('@/features/admin/pages/LocationsPage').then((m) => ({ default: m.LocationsPage })),
);
const CountriesPage = lazy(() =>
  import('@/features/admin/pages/CountriesPage').then((m) => ({ default: m.CountriesPage })),
);
const CurrenciesPage = lazy(() =>
  import('@/features/admin/pages/CurrenciesPage').then((m) => ({ default: m.CurrenciesPage })),
);
const LanguagesPage = lazy(() =>
  import('@/features/admin/pages/LanguagesPage').then((m) => ({ default: m.LanguagesPage })),
);
const CommunicationProvidersPage = lazy(() =>
  import('@/features/admin/pages/CommunicationProvidersPage').then((m) => ({
    default: m.CommunicationProvidersPage,
  })),
);
const CommunicationsPage = lazy(() =>
  import('@/features/communications/pages/CommunicationsPage').then((m) => ({
    default: m.CommunicationsPage,
  })),
);
const TrainingPage = lazy(() =>
  import('@/features/training/pages/TrainingPage').then((m) => ({ default: m.TrainingPage })),
);
const CohortDetailPage = lazy(() =>
  import('@/features/training/pages/CohortDetailPage').then((m) => ({
    default: m.CohortDetailPage,
  })),
);
const PastoralCarePage = lazy(() =>
  import('@/features/pastoral-care/pages/PastoralCarePage').then((m) => ({
    default: m.PastoralCarePage,
  })),
);
const CareCaseDetailPage = lazy(() =>
  import('@/features/pastoral-care/pages/CareCaseDetailPage').then((m) => ({
    default: m.CareCaseDetailPage,
  })),
);
const ExpensesPage = lazy(() =>
  import('@/features/expenses/pages/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const ExpenseDetailPage = lazy(() =>
  import('@/features/expenses/pages/ExpenseDetailPage').then((m) => ({
    default: m.ExpenseDetailPage,
  })),
);
const HrPage = lazy(() =>
  import('@/features/hr/pages/HrPage').then((m) => ({ default: m.HrPage })),
);
const HrDashboardPage = lazy(() =>
  import('@/features/hr/pages/HrDashboardPage').then((m) => ({ default: m.HrDashboardPage })),
);
const GivingPage = lazy(() =>
  import('@/features/giving/pages/GivingPage').then((m) => ({ default: m.GivingPage })),
);

function HomeRedirect() {
  const { role, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={roleHomePath(role)} replace />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path="/account" element={<AccountPage />} />

            <Route element={<RequireRole roles={ALL_PORTAL_ROLES} />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/members" element={<MembersPage />} />
              <Route path="/admin/members/import" element={<MemberImportPage />} />
              <Route path="/admin/members/:id" element={<MemberProfilePage />} />
              <Route path="/admin/zonal" element={<ZonalPage />} />
              <Route path="/admin/zonal/:id" element={<ZoneDetailPage />} />
              <Route path="/admin/departments" element={<DepartmentsPage />} />
              <Route path="/admin/departments/:id" element={<DepartmentDetailPage />} />
              <Route path="/admin/units/:id" element={<UnitDetailPage />} />
              <Route path="/admin/events" element={<EventsPage />} />
              <Route path="/admin/events/:id" element={<EventAttendancePage />} />
              <Route path="/admin/calendar" element={<ChurchCalendarPage />} />
              <Route path="/admin/calendar/import" element={<CalendarImportPage />} />
              <Route path="/admin/training" element={<TrainingPage />} />
              <Route path="/admin/training/cohorts/:id" element={<CohortDetailPage />} />
              <Route path="/admin/expenses" element={<ExpensesPage />} />
              <Route path="/admin/expenses/import" element={<ExpenseImportPage />} />
              <Route path="/admin/expenses/:id" element={<ExpenseDetailPage />} />
            </Route>

            <Route
              element={
                <RequireRole
                  roles={['super_admin', 'chief_pastor', 'associate_pastor', 'church_admin']}
                />
              }
            >
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/assets" element={<AssetsPage />} />
              <Route path="/admin/assets/import" element={<AssetImportPage />} />
              <Route path="/admin/assets/:id" element={<AssetDetailPage />} />
              <Route path="/admin/communications" element={<CommunicationsPage />} />
              <Route path="/admin/pastoral-care" element={<PastoralCarePage />} />
              <Route path="/admin/pastoral-care/:id" element={<CareCaseDetailPage />} />
              <Route path="/admin/hr" element={<HrPage />} />
              <Route path="/admin/hr/dashboard" element={<HrDashboardPage />} />
              <Route path="/admin/giving" element={<GivingPage />} />
            </Route>

            <Route element={<RequireRole roles={['super_admin']} />}>
              <Route path="/admin/organizations" element={<OrganizationsPage />} />
              <Route path="/admin/settings/locations" element={<LocationsPage />} />
              <Route path="/admin/settings/countries" element={<CountriesPage />} />
              <Route path="/admin/settings/currencies" element={<CurrenciesPage />} />
              <Route path="/admin/settings/languages" element={<LanguagesPage />} />
              <Route
                path="/admin/settings/communication-providers"
                element={<CommunicationProvidersPage />}
              />
            </Route>
          </Route>
        </Route>

        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </Suspense>
  );
}
